import api from "@/api/url";

export default {
    namespaced: true,

    state: () => {
        return {
            isAuthenticated: false,
            profile: {},
            logOutLoading: false,
            lastChecked: null,
            showNetworkError: false,
            retryCount: 0,
            shareableProfile: {},
        };
    },

    getters: {
        isAuthenticated: state => state.isAuthenticated,
        profile: state => state.profile,
        logOutLoading: state => state.logOutLoading,
        showNetworkError: state => state.showNetworkError,
        retryCount: state => state.retryCount,
        shareableProfile: state => state.shareableProfile,
    },

    mutations: {
        set(state, [key, value]) {
            state[key] = value;
        },
    },
    
    actions: {
        setAuthStatus({ commit }, bool) {
            commit("set", ["isAuthenticated", bool]);
        },

        setNetworkErr({ commit }, bool) {
            console.log("setNetworkErr", bool);
            
            commit("set", ["showNetworkError", bool]);
        },

        setRetryCount({ commit, state }, val) {
            if (val === "add") {
                const currentCount = state.retryCount + 1;
                commit("set", ["retryCount", currentCount]);
            } else {
                commit("set", ["retryCount", 0]);
            }
        },

        setLogoutLoading({ commit }, bool) {            
            commit("set", ["logOutLoading", bool]);
        },

        setLastChecked({ commit }, val) {
            commit("set", ["lastChecked", val]);
            localStorage.setItem("z:cached_time", val);
        },

        login(_, { email, password }) {
            return api.auth.login({ email, password })
                .then((resp) => {
                    const data = resp.data.data;
                    return data;
                });
        },

        signup(_, payload) {
            return api.auth.signup(payload)
                .then((resp) => {      
                    return resp?.data?.data;;
                });
        },

        externalLogin(_, payload) {
            return api.auth.externalLogin(payload)
                .then((resp) => {
                    return resp.data;
                });
        },

        signOut({ commit }) {
            return api.auth.logout()
                .then((resp) => {                    
                    commit("set", ["isAuthenticated", false]);
                    commit("set", ["profile", {}]);
                    commit("set", ["lastChecked",  null]);
                }); 
        },

        fetchProfile({ commit, state, dispatch }, { refresh, dataToMerge = null }) {
            if (state.profile?.email && !refresh) {
                dispatch("setAuthStatus", true);
                return state.profile;
            }

            if (dataToMerge) {
                commit("set", ["profile", { ...state.profile, ...dataToMerge }]);
                dispatch("setAuthStatus", true);
                return;
            }

            return api.user.get()
                .then((res) => {
                    const data = res.data.data;

                    commit("set", ["profile", data]);
                    dispatch("setAuthStatus", true);
                });
        },

        updateProfile({ commit, state }, payload) {
            return api.user.update(payload)
                .then((res) => {
                    const data = res.data.data;

                    commit("set", ["profile", { 
                        ...state.profile,
                        ...data, 
                    }]);
                });
        },

        deleteProfile(_) {
            return api.user.delete()
                .then((res) => {});
        },

        resetPassword(_, payload) {
            return api.auth.resetPassword(payload)
                .then((res) => {});
        },

        joinWaitlist(_, { email }) {
            return api.waitlist.joinWaitlist({ email })
                .then((res) => {
                    const data = res.data;
                    return data;
                });
        },

        async checkAuth({ dispatch, commit, state }) {
            try {
                const now = Date.now();
                const cachedTime = localStorage.getItem("z:cached_time");
                const parsedTime = Number(cachedTime) || 0;   
                    
                if (state.isAuthenticated) {
                    if (now < parsedTime) {
                        return false; // Use cached value
                    }
                    return true; // Use store value
                }
                
                await api.user.verifySession();
                
                commit("set", ["isAuthenticated", true]);
                commit("set", ["lastChecked", now]);
                localStorage.setItem("z:cached_time", now);

                return true;
            } catch (error) {                
                dispatch("setNetworkErr", true);
                commit("set", ["isAuthenticated", false]);
                commit("set", ["lastChecked", Date.now()]);
                localStorage.setItem("z:cached_time", Date.now());
                
                return false;
            }
        },

        connectCalendar(_, params) {
            return api.auth.connectCalendar(params)
                .then((res) => res.data.data);
        },

        connectResponseCb(_, params) {
            return api.auth.connectResponseCb(params)
                .then((res) => res);
        },

        updateCalendarProvider({ state, commit }, { payload, action }) {
            return api.user.updateCalendarProvider(action, payload)
                .then((res) => {
                    const data = res.data.data;

                    commit("set", ["profile", {
                        ...state.profile,
                        ...data,
                    }]);
                });
        },

        initiatePasswordRequest(_, payload) {
            return api.auth.initiatePasswordReset(payload)
                .then((res) => {
                    if (res) {
                        return res?.data?.data;
                    } 
                }).catch(error => {
                    throw error;
                });
        },

        generateSignedUrl(_, { payload, params }) {
            return api.app.getSignedUrl(payload, params)
                .then((res) => {
                    return res.data.data;
                });
        },

        validateMockSignedUrl(_, { config, payload }) {      
            return api.app.validateMockSignedUrl(payload, config)
                .then((resp) => {
                    return resp;
                });
        },

        removeUpload({ state, commit }, payload) {      
            return api.app.removeUpload(payload)
                .then((resp) => {
                    const data = resp.data.data;

                    commit("set", ["profile", {
                        ...state.profile,
                        ...data,
                    }]);
                });
        },

        addLink({ commit, state }, { payload, type }) {
            return api.user.addLinks(payload, { type })
                .then((res) => {
                    const data = res.data.data;
                    commit("set", ["profile", {
                        ...state.profile,
                        ...data,
                    }]);
                });
        },

        updateLink({ commit, state }, { payload, type }) {
            return api.user.updateLink(payload._id, payload, { type })
                .then((res) => {
                    // const updatedItem = state.profile[`${type}`].map(x => {
                    //     if (x && x._id !== payload._id) return x;
                    //     return { ...x, payload };
                    // });

                    commit("set", ["profile", {
                        ...state.profile,
                        // [`${type}`]: updatedItem,
                        ...res.data.data,
                    }]);
                });
        },

        deleteLink({ commit, state }, { id, type }) {
            return api.user.deleteLink(id, { type })
                .then((res) => {
                    commit("set", ["profile", {
                        ...state.profile,
                        ...res.data.data,
                    }]);
                });
        },

        generatePublicUrl({ commit, state }) {
            return api.user.generatePublicUrl()
                .then((res) => {
                    commit("set", ["profile", {
                        ...state.shareableProfile,
                        ...res.data.data,
                    }]);
                });
        },

        fetchPublicPageData({ commit, state }) {
            return api.user.fetchPublicPageData()
                .then((res) => {
                    commit("set", ["shareableProfile", res.data.data]);
                });
        },

        manageSkills({ commit, state }, payload) {
            return api.user.manageSkills(payload)
                .then((res) => {
                    commit("set", ["profile", {
                        ...state.profile,
                        ...res.data.data,
                    }]);
                });
        },

        updatePaymentLinks({ state, commit }, { type, payload }) {  
            return api.user.updatePaymentLinks(payload, { type })
                .then((resp) => {                    
                    commit("set", ["profile", {
                        ...state.profile,
                        paymentLinks: resp.data.data,
                    }]);

                    return resp.data.data;
                });
        },

        updatePaymentAccounts({ state, commit }, { type, payload }) {            
            return api.user.updateAccounts(payload, { type })
                .then((resp) => {
                    let res = {};
                    
                    switch (type) {
                    case "add":
                        res = [ ...resp.data.data, ...state.profile.accounts];
                        commit("set", ["profile", {
                            ...state.profile,
                            accounts: res,
                        }]);
                        break;

                    case "edit":
                        const accounts = state.accounts;
                        res = accounts.map(x => {
                            if (x._id === resp.data.data?._id) {
                                return {
                                    ...x,
                                    ...resp.data.data,
                                };
                            }

                            return x;
                        });
                        
                        commit("set", ["profile", {
                            ...state.profile,
                            accounts: res,
                        }]);
                        break;
                    
                    default:
                        break;
                    }

                    return res;
                });
        },
    },
};